import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { DateTime } from 'luxon';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getRelativeTimeSimple(dateString: string): string {
  if (!dateString) {
    return 'just now';
  }

  // Check for default/null date values
  if (
    dateString.startsWith('0001-01-01') ||
    dateString === '0001-01-01T00:00:00Z'
  ) {
    return 'recently';
  }

  try {
    let date = DateTime.fromISO(dateString);

    // If invalid, try different formats
    if (!date.isValid) {
      // Try SQL datetime format
      date = DateTime.fromSQL(dateString);
    }

    if (!date.isValid) {
      // Try JavaScript Date parsing as fallback
      const jsDate = new Date(dateString);
      if (!isNaN(jsDate.getTime())) {
        date = DateTime.fromJSDate(jsDate);
      }
    }

    // If still invalid, return fallback
    if (!date.isValid) {
      return 'recently';
    }

    // Check if date is year 0001 or very old (database default values)
    if (date.year <= 1900) {
      return 'recently';
    }

    // Use English locale for relative time
    const relative = date.toRelative({ locale: 'en' });
    return relative || 'just now';
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return 'recently';
  }
}

/**
 * Truncates a text string to a specified maximum length and adds ellipsis if needed.
 *
 * @param text - The text string to be truncated
 * @param maxLength - The maximum number of characters allowed (must be positive)
 * @returns The truncated string with "..." appended if truncation occurred,
 *          or the original string if it's within the limit
 *
 * @example
 * ```typescript
 * truncateText("Hello world, this is a long text", 10);
 * // Returns: "Hello w..."
 *
 * truncateText("Short", 10);
 * // Returns: "Short"
 *
 * truncateText("", 5);
 * // Returns: ""
 * ```
 */
export function truncateText(text: string, maxLength: number): string {
  // Input validation
  if (typeof text !== 'string') {
    throw new Error('Text parameter must be a string');
  }

  if (typeof maxLength !== 'number' || maxLength < 0) {
    throw new Error('MaxLength parameter must be a non-negative number');
  }

  // Handle edge cases
  if (maxLength === 0) {
    return '';
  }

  if (text.length <= maxLength) {
    return text;
  }

  // If the max length is less than 3, we can't fit the ellipsis properly
  if (maxLength < 3) {
    return text.substring(0, maxLength);
  }

  // Truncate and add ellipsis
  return text.substring(0, maxLength - 3) + '...';
}
